import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { categoryService } from '../../services/api';
import { CategoryWithStats } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AddButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const CategoryCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CategoryIcon = styled.div<{ color: string }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryName = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.1rem;
`;

const CategorySlug = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const CategoryDescription = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
  line-height: 1.4;
`;

const CategoryStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const PropertyCount = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const CategoryActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' | 'featured' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'edit':
        return `
          background-color: ${theme.colors.primary};
          color: white;
          &:hover { background-color: ${theme.colors.secondary}; }
        `;
      case 'delete':
        return `
          background-color: #ef4444;
          color: white;
          &:hover { background-color: #dc2626; }
        `;
      case 'featured':
        return `
          background-color: #f59e0b;
          color: white;
          &:hover { background-color: #d97706; }
        `;
      default:
        return `
          background-color: ${theme.colors.surface};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border};
          &:hover { background-color: ${theme.colors.background}; }
        `;
    }
  }}
`;

const Badge = styled.span<{ $featured?: boolean; $active?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  
  ${({ $featured, $active, theme }) => {
    if ($featured) {
      return `
        background-color: #f59e0b;
        color: white;
      `;
    }
    if ($active) {
      return `
        background-color: #10b981;
        color: white;
      `;
    }
    return `
      background-color: #6b7280;
      color: white;
    `;
  }}
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
  resize: vertical;
  min-height: 80px;
`;

const ColorInput = styled.input`
  width: 50px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-weight: 500;
  
  ${({ variant, theme }) => {
    if (variant === 'primary') {
      return `
        background-color: ${theme.colors.primary};
        color: white;
        &:hover { background-color: ${theme.colors.secondary}; }
      `;
    }
    return `
      background-color: ${theme.colors.surface};
      color: ${theme.colors.text.primary};
      border: 1px solid ${theme.colors.border};
      &:hover { background-color: ${theme.colors.background}; }
    `;
  }}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface CategoryForm {
  name: string;
  slug: string;
  description: string;
  color: string;
  icon: string;
  is_active: boolean;
  is_featured: boolean;
  order_index: number;
}

const AdminCategories: React.FC = () => {
  const { showNotification } = useNotification();
  const [categories, setCategories] = useState<CategoryWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryWithStats | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; category: CategoryWithStats | null }>({
    isOpen: false,
    category: null
  });

  const [formData, setFormData] = useState<CategoryForm>({
    name: '',
    slug: '',
    description: '',
    color: '#3b82f6',
    icon: '',
    is_active: true,
    is_featured: false,
    order_index: 0
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      showNotification('Erro ao carregar categorias', 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryService.update(editingCategory.id, formData);
        showNotification('Categoria atualizada com sucesso!', 'success');
      } else {
        await categoryService.create(formData);
        showNotification('Categoria criada com sucesso!', 'success');
      }
      
      setModalOpen(false);
      setEditingCategory(null);
      resetForm();
      await loadCategories();
    } catch (error: any) {
      console.error('Erro ao salvar categoria:', error);
      const message = error.response?.data?.detail || 'Erro ao salvar categoria';
      showNotification(message, 'error');
    }
  };

  const handleEdit = (category: CategoryWithStats) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      color: category.color,
      icon: category.icon || '',
      is_active: category.is_active,
      is_featured: category.is_featured,
      order_index: category.order_index
    });
    setModalOpen(true);
  };

  const handleDelete = (category: CategoryWithStats) => {
    setDeleteModal({ isOpen: true, category });
  };

  const confirmDelete = async () => {
    if (!deleteModal.category) return;

    try {
      await categoryService.delete(deleteModal.category.id);
      showNotification('Categoria excluída com sucesso!', 'success');
      setDeleteModal({ isOpen: false, category: null });
      await loadCategories();
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      showNotification('Erro ao excluir categoria', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      color: '#3b82f6',
      icon: '',
      is_active: true,
      is_featured: false,
      order_index: 0
    });
  };

  const handleOpenModal = () => {
    resetForm();
    setEditingCategory(null);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Carregando categorias...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Gerenciar Categorias</Title>
        <AddButton onClick={handleOpenModal}>
          + Nova Categoria
        </AddButton>
      </Header>

      <CategoriesGrid>
        {categories.map(category => (
          <CategoryCard key={category.id}>
            <CategoryHeader>
              <CategoryIcon color={category.color}>
                {category.icon || '🏠'}
              </CategoryIcon>
              <CategoryInfo>
                <CategoryName>{category.name}</CategoryName>
                <CategorySlug>/{category.slug}</CategorySlug>
              </CategoryInfo>
              <div>
                {category.is_featured && <Badge $featured>Destaque</Badge>}
                <Badge $active={category.is_active}>
                  {category.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            </CategoryHeader>
            
            {category.description && (
              <CategoryDescription>{category.description}</CategoryDescription>
            )}
            
            <CategoryStats>
              <PropertyCount>
                {category.property_count} {category.property_count === 1 ? 'imóvel' : 'imóveis'}
              </PropertyCount>
              <CategoryActions>
                <ActionButton variant="edit" onClick={() => handleEdit(category)}>
                  Editar
                </ActionButton>
                <ActionButton variant="delete" onClick={() => handleDelete(category)}>
                  Excluir
                </ActionButton>
              </CategoryActions>
            </CategoryStats>
          </CategoryCard>
        ))}
      </CategoriesGrid>

      {categories.length === 0 && (
        <LoadingMessage>
          Nenhuma categoria encontrada. Clique em "Nova Categoria" para criar a primeira.
        </LoadingMessage>
      )}

      {/* Modal de Formulário */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>Nome *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
                placeholder="Ex: Casas de Luxo"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Slug *</Label>
              <Input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                required
                placeholder="casas-luxo"
              />
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label>Descrição</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrição da categoria..."
            />
          </FormGroup>

          <FormGrid>
            <FormGroup>
              <Label>Cor</Label>
              <ColorInput
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Ícone (emoji)</Label>
              <Input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                placeholder="🏰"
                maxLength={4}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              <Label>Categoria ativa</Label>
            </CheckboxGroup>
            
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
              />
              <Label>Destacar na home</Label>
            </CheckboxGroup>
          </FormGrid>

          <FormGroup>
            <Label>Ordem de exibição</Label>
            <Input
              type="number"
              value={formData.order_index}
              onChange={(e) => setFormData(prev => ({ ...prev, order_index: parseInt(e.target.value) || 0 }))}
              min="0"
            />
          </FormGroup>

          <FormActions>
            <Button type="button" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              {editingCategory ? 'Atualizar' : 'Criar'}
            </Button>
          </FormActions>
        </form>
      </Modal>

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, category: null })}
        onConfirm={confirmDelete}
        title="Excluir Categoria"
        message={`Tem certeza que deseja excluir a categoria "${deleteModal.category?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </Container>
  );
};

export default AdminCategories; 
